import React from "react";
import { Search, Filter, RotateCcw, X } from "lucide-react";

/**
 * Standardized FilterBar component for table pages
 * @param {string} searchValue - Current search query
 * @param {function} onSearchChange - Function when search input changes
 * @param {Array<{id: string, label: string, value: string, options: Array<{value: string, label: string}>, onChange: function}>} filters - Array of select filter objects
 * @param {function} onReset - Function to reset all filters
 * @param {string} placeholder - Search placeholder text
 */
const FilterBar = ({
  searchValue = "",
  onSearchChange = () => {},
  filters = [],
  onReset = null,
  placeholder = "Rechercher..."
}) => {
  const activeFiltersCount = filters.filter(f => f.value && f.value !== "ALL" && f.value !== "").length + (searchValue ? 1 : 0);

  return (
    <div className="ui-card" style={{
      padding: "16px",
      marginBottom: "20px",
      display: "flex",
      gap: "12px",
      alignItems: "center",
      flexWrap: "wrap",
      backgroundColor: "var(--bg-card)"
    }}>
      {/* Search Input */}
      <div style={{
        position: "relative",
        flex: "1 1 240px",
        minWidth: "200px"
      }}>
        <Search
          size={18}
          style={{
            position: "absolute",
            left: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--text-muted)"
          }}
        />
        <input
          type="text"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={placeholder}
          style={{
            width: "100%",
            padding: "9px 12px 9px 38px",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border)",
            backgroundColor: "var(--bg-main)",
            fontSize: "0.9rem",
            color: "var(--text-main)",
            transition: "var(--transition-fast)"
          }}
        />
        {searchValue && (
          <button
            onClick={() => onSearchChange("")}
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--text-muted)",
              padding: "2px"
            }}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Select Filters */}
      {filters.map((filter) => (
        <div key={filter.id} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {filter.label && (
            <span style={{ fontSize: "0.85rem", fontWeight: "500", color: "var(--text-muted)" }}>
              {filter.label}:
            </span>
          )}
          <select
            value={filter.value}
            onChange={(e) => filter.onChange(e.target.value)}
            style={{
              padding: "9px 12px",
              borderRadius: "var(--radius-md)",
              border: filter.value && filter.value !== "ALL" && filter.value !== "" 
                ? "1.5px solid var(--primary)" 
                : "1px solid var(--border)",
              backgroundColor: "var(--bg-main)",
              fontSize: "0.88rem",
              fontWeight: filter.value && filter.value !== "ALL" && filter.value !== "" ? "600" : "400",
              color: "var(--text-main)",
              cursor: "pointer",
              transition: "var(--transition-fast)"
            }}
          >
            {filter.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      ))}

      {/* Active Filters Badge & Reset Button */}
      {activeFiltersCount > 0 && onReset && (
        <button
          onClick={onReset}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "8px 12px",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--status-rejected-border)",
            backgroundColor: "var(--status-rejected-bg)",
            color: "var(--status-rejected-text)",
            fontSize: "0.85rem",
            fontWeight: "600",
            cursor: "pointer",
            transition: "var(--transition-fast)",
            marginLeft: "auto"
          }}
        >
          <RotateCcw size={14} />
          Réinitialiser ({activeFiltersCount})
        </button>
      )}
    </div>
  );
};

export default FilterBar;
